import {
  AppEndpointAddonMasterRaw,
  AppEndpointAddonMasterRawForm,
  AppEndpointAddonMasterUpdateRawForm,
} from "@/domain/AppEndpointAddonMaster";
import { API_CLIENT } from "../config";
import { getAPIConfigs } from "../helpers";

const apiUrlMasters = "admin/app_config/masters/endpoint_addons";

export const getAdminAppEndpointAddonMasters = async (): Promise<
  AppEndpointAddonMasterRaw[]
> => {
  const response = await API_CLIENT.get<{ items: AppEndpointAddonMasterRaw[] }>(
    `${apiUrlMasters}`
  );
  return response.data.items;
};

export const getAdminAppEndpointAddonMaster = async ({
  endpointAddonMasterId,
}: {
  endpointAddonMasterId: AppEndpointAddonMasterRaw["id"];
}): Promise<AppEndpointAddonMasterRaw> => {
  const response = await API_CLIENT.get<{ item: AppEndpointAddonMasterRaw }>(
    `${apiUrlMasters}/${endpointAddonMasterId}`
  );
  return response.data.item;
};

export const postAdminCreateAppEndpointAddonMaster = async ({
  organizationId,
  appEndpointAddonMasterRawForm,
}: {
  organizationId?: string;
  appEndpointAddonMasterRawForm: AppEndpointAddonMasterRawForm;
}): Promise<AppEndpointAddonMasterRaw> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const response = await API_CLIENT.post<{ item: AppEndpointAddonMasterRaw }>(
    `${apiUrlMasters}`,
    appEndpointAddonMasterRawForm,
    {
      ...apiOrganizationIdParams,
    }
  );
  return response.data.item;
};

export const postAdminEditAppEndpointAddonMaster = async ({
  endpointAddonMasterId,
  appEndpointAddonMasterUpdateRawForm,
}: {
  endpointAddonMasterId: AppEndpointAddonMasterRaw["id"];
  appEndpointAddonMasterUpdateRawForm: AppEndpointAddonMasterUpdateRawForm;
}): Promise<AppEndpointAddonMasterRaw> => {
  const response = await API_CLIENT.post<{ item: AppEndpointAddonMasterRaw }>(
    `${apiUrlMasters}/${endpointAddonMasterId}`,
    appEndpointAddonMasterUpdateRawForm
  );
  return response.data.item;
};

// AppEndpointAddonMaster の cloud_run_service_config.secret_env_vars に対応する
// Secret Manager 上の現在値を取得する。値の閲覧は aa:developer 以上に制限される。
// Endpoint Addon 本体のシークレット取得 API と同じく name → value のマップを返す。
export const getAdminAppEndpointAddonMasterSecrets = async ({
  endpointAddonMasterId,
}: {
  endpointAddonMasterId: AppEndpointAddonMasterRaw["id"];
}): Promise<{ [k: string]: string }> => {
  const response = await API_CLIENT.get<{ item: { [k: string]: string } }>(
    `${apiUrlMasters}/${endpointAddonMasterId}/secrets`
  );
  return response.data.item;
};

// AppEndpointAddonMaster の secret_env_vars を送信したマップで全置換する (全置換方式)。
// マスタ変更は各 Endpoint Addon の再デプロイ時に反映される (本 API では再デプロイしない)。
// 書き込みは aa:developer 以上に制限される。
export const postAdminAppEndpointAddonMasterSecrets = async ({
  endpointAddonMasterId,
  item,
}: {
  endpointAddonMasterId: AppEndpointAddonMasterRaw["id"];
  item: { [k: string]: string };
}): Promise<AppEndpointAddonMasterRaw> => {
  const response = await API_CLIENT.post<{ item: AppEndpointAddonMasterRaw }>(
    `${apiUrlMasters}/${endpointAddonMasterId}/secrets`,
    { item }
  );
  return response.data.item;
};

export const postAdminDisableAppEndpointAddonMaster = async ({
  endpointAddonMasterId,
}: {
  endpointAddonMasterId: AppEndpointAddonMasterRaw["id"];
}): Promise<AppEndpointAddonMasterRaw> => {
  const response = await API_CLIENT.post<{ item: AppEndpointAddonMasterRaw }>(
    `${apiUrlMasters}/${endpointAddonMasterId}/delete`,
    undefined
  );
  return response.data.item;
};

export const postAdminEnableAppEndpointAddonMaster = async ({
  endpointAddonMasterId,
}: {
  endpointAddonMasterId: AppEndpointAddonMasterRaw["id"];
}): Promise<AppEndpointAddonMasterRaw> => {
  const response = await API_CLIENT.post<{ item: AppEndpointAddonMasterRaw }>(
    `${apiUrlMasters}/${endpointAddonMasterId}/enable`,
    undefined
  );
  return response.data.item;
};

export const postAdminRemoveAppEndpointAddonMaster = async ({
  endpointAddonMasterId,
}: {
  endpointAddonMasterId: AppEndpointAddonMasterRaw["id"];
}): Promise<void> => {
  await API_CLIENT.post(`${apiUrlMasters}/${endpointAddonMasterId}/remove`);
};
